import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ImportMemberResult } from '@core/api/services/portal-member-import.service';

export interface ImportObservationGroup {
  title: string;
  observations: { message: string; needsAttention: boolean }[];
  attentionCount: number;
}

export function groupImportObservations(messages: string[]): ImportObservationGroup[] {
  const groups: ImportObservationGroup[] = [
    { title: 'Información personal', observations: [], attentionCount: 0 },
    { title: 'Actividad Ministerial', observations: [], attentionCount: 0 },
    { title: 'Colaboraciones', observations: [], attentionCount: 0 },
    { title: 'Pases de comunidad', observations: [], attentionCount: 0 },
    { title: 'Eventos civiles y religiosos', observations: [], attentionCount: 0 },
  ];
  const displayedMessages: string[] = [];
  const unmappedEvents: string[] = [];
  const importedEvents: string[] = [];
  for (const message of messages) {
    if (message === 'Historial de direcciones, contactos no elegidos y campos sin destino: conservados en el lote.') continue;
    const unmapped = /^Sin equivalente religioso: (.+)\. Historia civil conservada en el lote\.$/.exec(message);
    if (unmapped) {
      unmappedEvents.push(unmapped[1]);
      continue;
    }
    if (message.startsWith('Importado como evento religioso: ')) {
      importedEvents.push(message.slice('Importado como evento religioso: '.length).replace(/ \(\d{4}-\d{2}-\d{2}\)\.$/, ''));
      continue;
    }
    const unlinked = /^Persona relacionada sin vincular para (.+?)(?:;.*|\.)$/.exec(message);
    displayedMessages.push(unlinked ? `Persona pendiente de vincular en ${unlinked[1]}.` : message);
  }
  if (importedEvents.length) displayedMessages.push(`${importedEvents.length} evento${importedEvents.length === 1 ? '' : 's'} importado${importedEvents.length === 1 ? '' : 's'}: ${[...new Set(importedEvents)].join(', ')}.`);
  if (unmappedEvents.length) displayedMessages.push(`${unmappedEvents.length} evento${unmappedEvents.length === 1 ? '' : 's'} sin equivalencia: ${[...new Set(unmappedEvents)].join(', ')}.`);
  for (const message of displayedMessages) {
    const normalized = message.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const index = /colaboracion|encargo|nombramiento/.test(normalized) ? 2
      : /ministerios|historial ministerial|acontecimientos ministeriales/.test(normalized) ? 1
      : /historial de comunidad|historia de comunidad|pase de comunidad/.test(normalized) ? 3
      : /acontecimiento|evento|equivalente religioso|historia civil|persona relacionada|persona pendiente de vincular en|lugar demasiado largo|observaciones demasiado largas|ya tiene otra fecha|fecha fuera de la vida|bautismo|matrimonio|bendicion|confirmacion|santo sellamiento/.test(normalized) ? 4 : 0;
    const needsAttention = /pendiente|conflicto|sin vincular|se omitio|no se import|requiere revision|revis[aá]|no se sobrescribio|no se vinculo|ya pertenece|eliminada|fusionada|comunidad activa valida/.test(normalized);
    groups[index].observations.push({ message, needsAttention });
    if (needsAttention) groups[index].attentionCount++;
  }
  return groups.filter(group => group.observations.length > 0);
}

@Component({
  selector: 'app-portal-import-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, RouterLink, MatButtonModule, MatIconModule],
  templateUrl: './portal-import-result.component.html',
  styleUrl: './portal-import-result.component.scss',
})
export class PortalImportResultComponent {
  @Input({ required: true }) summary!: { created: number; skipped: number; warnings: string[] };
  @Input() fileName = '';
  @Input() memberResults: ImportMemberResult[] = [];
  @Input() memberCommunities: Record<string, { id: string; name: string }> = {};
  @Input() isAdmin = false;
  @Input() restoringPersonId?: string;
  @Output() restore = new EventEmitter<ImportMemberResult>();
  search = '';
  onlyPending = false;
  selectedMemberId?: string;
  mobileDetailOpen = false;

  get filteredMembers(): ImportMemberResult[] {
    const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const query = normalize(this.search.trim());
    return this.memberResults.filter(item => (!query || normalize(item.fullName).includes(query) || normalize(item.portalMemberId).includes(query))
      && (!this.onlyPending || item.status === 'Bloqueado' || this.attentionCount(item) > 0));
  }

  get selectedMember(): ImportMemberResult | undefined {
    return this.filteredMembers.find(item => item.portalMemberId === this.selectedMemberId) ?? this.filteredMembers[0];
  }

  get communityGroups(): { id: string; name: string; members: ImportMemberResult[] }[] {
    const groups = new Map<string, { id: string; name: string; members: ImportMemberResult[] }>();
    for (const member of this.filteredMembers) {
      const community = this.memberCommunities[member.portalMemberId] ?? { id: '', name: 'Comunidad sin identificar' };
      let group = groups.get(community.id);
      if (!group) { group = { ...community, members: [] }; groups.set(community.id, group); }
      group.members.push(member);
    }
    return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }
  trackCommunity(_: number, group: { id: string }): string { return group.id; }

  selectMember(item: ImportMemberResult): void {
    this.selectedMemberId = item.portalMemberId;
    this.mobileDetailOpen = true;
  }
  observationGroups(item: ImportMemberResult): ImportObservationGroup[] {
    return groupImportObservations(item.messages);
  }
  attentionCount(item: ImportMemberResult): number {
    return this.observationGroups(item).reduce((total, group) => total + group.attentionCount, 0);
  }
}
